export type SettingsAudience = 'personal' | 'member' | 'admin' | 'owner';
export type SettingsApp = 'mail' | 'calendar' | 'tasks' | 'notes' | 'contacts' | 'files';

export type SettingsPage =
  | 'account'
  | 'time-date'
  | 'notifications'
  | 'usage'
  | 'security'
  | 'billing-personal'
  | 'payment-methods-personal'
  | 'shared-items'
  | 'shared-workspace'
  | 'team-members'
  | 'team-usage'
  | 'billing-team'
  | 'payment-methods-team'
  | 'workspace'
  | 'mail-permissions'
  | 'mail-import'
  | 'mail-signatures'
  | 'mail-auto-reply'
  | 'mail-templates'
  | 'mail-general'
  | 'team-mail-permissions'
  | 'team-mail-inbox'
  | 'calendar'
  | 'tasks';

export interface SettingsMenuRow {
  readonly id: SettingsPage | SettingsApp;
  readonly label: string;
  readonly path: string;
  readonly kind: 'page' | 'app';
  readonly icon?: string;
  readonly children?: readonly SettingsMenuRow[];
}

/**
 * A team login's app settings come in two sections (Pavel + Johanna, mysettings r1, 2026-11-05: "2 separate sections - My
 * apps, Team apps"): My apps edits the person's own workspace inside the team, Team apps the team's. A login with no team
 * keeps the one section "Apps", unchanged.
 */
export type SettingsAppsGroupLabel = 'Apps' | 'My apps' | 'Team apps';

export interface SettingsMenuGroup {
  readonly label: 'Account' | 'Team workspace' | SettingsAppsGroupLabel;
  readonly rows: readonly SettingsMenuRow[];
}

export type PageExists = (page: SettingsPage) => boolean;
export type CanSeeApp = (app: SettingsApp) => boolean;

export const SHIPPED_SETTINGS_PAGES = new Set<SettingsPage>([
  'account',
  'time-date',
  'notifications',
  'usage',
  'security',
  'shared-items',
  'shared-workspace',
  'team-members',
  'team-usage',
  'billing-personal',
  'billing-team',
  'payment-methods-personal',
  'payment-methods-team',
  'workspace',
  'mail-permissions',
  'mail-import',
  'mail-signatures',
  'mail-auto-reply',
  'mail-templates',
  'mail-general',
  'team-mail-permissions',
  'team-mail-inbox',
  'calendar',
  'tasks',
]);

/** The route prefix of My apps' pages (`my/mail/permissions`): a team login's own workspace. */
export const MY_APPS_PREFIX = 'my/';
/** The route prefix of Team apps' pages (`team/mail/permissions`), beside Team workspace's `team/people`. */
export const TEAM_APPS_PREFIX = 'team/';

/** The pages a team login reaches under `my/…` that a personal login reaches bare: Mail's, Calendar's and Tasks'. */
const MY_APPS_SECTION = /^(mail|calendar|tasks)(\/|$)/;

/**
 * Where an app settings address of the window lands for this login: a team login's bare `mail/…`, `calendar` or
 * `tasks` (an old link, a bookmark) is its My apps page under `my/…`; a personal login's `my/…` is its bare page.
 * `undefined` means the address needs no change.
 */
export function settingsAppsAddress(path: string, teamLogin: boolean): string | undefined {
  if (teamLogin) {
    return MY_APPS_SECTION.test(path) ? MY_APPS_PREFIX + path : undefined;
  }
  return path.startsWith(MY_APPS_PREFIX) && MY_APPS_SECTION.test(path.slice(MY_APPS_PREFIX.length))
    ? path.slice(MY_APPS_PREFIX.length)
    : undefined;
}

/**
 * `teamLogin` is the login's kind, not the audience: Notifications is Chat's notification page, and a
 * login that is not known to be a team one has no Chat to be notified by.
 */
export function shippedSettingsPages(audience: SettingsAudience, teamLogin: boolean): PageExists {
  return (page) =>
    SHIPPED_SETTINGS_PAGES.has(page) &&
    (page !== 'team-usage' || audience === 'owner' || audience === 'admin') &&
    (page !== 'notifications' || teamLogin);
}

function flattenMenuRows(rows: readonly SettingsMenuRow[]): readonly SettingsMenuRow[] {
  return rows.flatMap((row) => [row, ...flattenMenuRows(row.children ?? [])]);
}

/** Every shipped menu path visible to one audience, including app children. */
export function settingsVisiblePaths(
  audience: SettingsAudience,
  canSeeApp: CanSeeApp,
  teamLogin: boolean,
): ReadonlySet<string> {
  return new Set(
    settingsMenu(audience, shippedSettingsPages(audience, teamLogin), canSeeApp)
      .flatMap(({ rows }) => flattenMenuRows(rows))
      .map(({ path }) => path),
  );
}

/** Whether the same rule that renders the menu permits opening this settings path. */
export function settingsPageAllowed(
  path: string,
  audience: SettingsAudience,
  canSeeApp: CanSeeApp,
  teamLogin: boolean,
): boolean {
  return settingsVisiblePaths(audience, canSeeApp, teamLogin).has(path);
}

const page = (id: SettingsPage, label: string, path: string): SettingsMenuRow => ({ id, label, path, kind: 'page' });

/** Mail's pages in Apps (`prefix` '') or My apps (`my/`). */
const mailChildren = (prefix: string): readonly SettingsMenuRow[] => [
  page('mail-permissions', 'Email Permissions', `${prefix}mail/permissions`),
  page('mail-import', 'Import', `${prefix}mail/import`),
  page('mail-signatures', 'Signatures', `${prefix}mail/signatures`),
  page('mail-auto-reply', 'Auto Reply', `${prefix}mail/auto-reply`),
  page('mail-templates', 'Templates', `${prefix}mail/templates`),
  page('mail-general', 'General', `${prefix}mail/general`),
];

/**
 * Team apps › Mail: the team's own mail settings (Email Permissions: who may reach the team's mailbox, its blocked
 * senders) and the member's own level for the team's Inbox. Every member sees them; only the Owner and Admins can
 * change the first (the page says so), while the second is each member's own.
 */
const teamMailChildren: readonly SettingsMenuRow[] = [
  page('team-mail-permissions', 'Email Permissions', `${TEAM_APPS_PREFIX}mail/permissions`),
  page('team-mail-inbox', 'Mail volume · Team Inbox', `${TEAM_APPS_PREFIX}mail/team-inbox`),
];

const app = (
  id: SettingsApp,
  label: string,
  path: string,
  icon: string,
  children?: readonly SettingsMenuRow[],
): SettingsMenuRow => ({ id, label, path, icon, kind: 'app', children });

export function settingsMenu(
  audience: SettingsAudience,
  pageExists: PageExists,
  canSeeApp: CanSeeApp,
): readonly SettingsMenuGroup[] {
  const accountRows: readonly SettingsMenuRow[] = [
    page('account', 'Account', 'general/account'),
    page('time-date', 'Time & Date', 'general/time'),
    page('notifications', 'Notifications', 'general/chat-notifications'),
    page('usage', 'Usage', 'general/usage'),
    page('security', 'Security', 'general/security'),
    ...(audience === 'personal'
      ? [
          page('shared-items', 'Shared items', 'general/shared-items'),
          page('billing-personal', 'Billing', 'general/billing'),
          page('payment-methods-personal', 'Payment Methods', 'general/payment-methods'),
        ]
      : [page('shared-workspace', 'Shared workspace', 'general/shared-workspace')]),
  ];

  const teamRows: readonly SettingsMenuRow[] =
    audience === 'admin'
      ? [page('team-members', 'Team members', 'team/people'), page('team-usage', 'Team usage', 'team/usage')]
      : audience === 'owner'
        ? [
            page('team-members', 'Team members', 'team/people'),
            page('team-usage', 'Team usage', 'team/usage'),
            page('billing-team', 'Billing', 'team/billing'),
            page('payment-methods-team', 'Payment Methods', 'team/payment-methods'),
            page('workspace', 'Workspace', 'team/workspace'),
          ]
        : [];

  // A team login's own apps live under `my/…`, so they never share an address with the team's (`team/…`).
  const prefix = audience === 'personal' ? '' : MY_APPS_PREFIX;
  const apps: readonly SettingsMenuRow[] = [
    app('mail', 'Mail', `${prefix}mail`, 'sapphire-email', mailChildren(prefix)),
    app('calendar', 'Calendar', `${prefix}calendar`, 'sapphire-calendars'),
    app('contacts', 'Contacts', 'contacts', 'sapphire-contact'),
    app('tasks', 'Tasks', `${prefix}tasks`, 'sapphire-tasks'),
    app('notes', 'Notes', 'notes', 'sapphire-notebook'),
    app('files', 'Files', 'files', 'sapphire-file'),
  ];
  // Team apps: every member sees them; only the Owner and Admins change them (each page says so).
  const teamApps: readonly SettingsMenuRow[] =
    audience === 'personal'
      ? []
      : [
          app('mail', 'Mail', `${TEAM_APPS_PREFIX}mail`, 'sapphire-email', teamMailChildren),
          app('calendar', 'Calendar', `${TEAM_APPS_PREFIX}calendar`, 'sapphire-calendars'),
          app('tasks', 'Tasks', `${TEAM_APPS_PREFIX}tasks`, 'sapphire-tasks'),
        ];

  const visibleApps = (rows: readonly SettingsMenuRow[]): readonly SettingsMenuRow[] =>
    rows
      .filter((row) => canSeeApp(row.id as SettingsApp))
      .map((row) => ({
        ...row,
        children: row.children?.filter((child) => pageExists(child.id as SettingsPage)),
      }))
      .filter((row) => (row.children?.length ?? 0) > 0 || pageExists(row.id as SettingsPage));

  const groups: SettingsMenuGroup[] = [
    { label: 'Account', rows: accountRows.filter((row) => pageExists(row.id as SettingsPage)) },
    ...(teamRows.length > 0
      ? [{ label: 'Team workspace' as const, rows: teamRows.filter((row) => pageExists(row.id as SettingsPage)) }]
      : []),
    { label: audience === 'personal' ? 'Apps' : 'My apps', rows: visibleApps(apps) },
    ...(teamApps.length > 0 ? [{ label: 'Team apps' as const, rows: visibleApps(teamApps) }] : []),
  ];
  return groups.filter((group) => group.rows.length > 0);
}
